import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Check, Link2, QrCode } from "lucide-react"
import { Button } from "@/components/ui/button"
import ItemCover from "@/components/ItemCover"
import { type IntegrationQrCodeResponse } from "@/services/adminApi"
import { capturePlatformEvent } from "@/services/platformAnalytics"

const downloadDataUrl = (filename: string, dataUrl: string) => {
  const anchor = document.createElement("a")
  anchor.href = dataUrl
  anchor.download = filename
  anchor.click()
}

export const IntegrationQrCodeResult = ({
  result,
  productName,
  thumbnailSrc,
}: {
  result: IntegrationQrCodeResponse
  productName?: string
  /** Optional product cover, shown as a small thumbnail next to the title. */
  thumbnailSrc?: string
}) => {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  const shareLink = result.targetUrl || result.shortLink
  const title = productName?.trim() || t("integrationInfo.qrCode.shareCatalogTitle")

  // Event names are the analytics contract from Asana 1216906021215411 — keep verbatim.
  const shareEventProperties = () => ({
    product_id: result.target.type === "product" ? result.target.productId : undefined,
    short_code: result.shortCode,
    target_type: result.target.type,
  })

  const handleCopy = async () => {
    capturePlatformEvent("get_link_taped", shareEventProperties())
    try {
      await navigator.clipboard.writeText(shareLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // clipboard write failed — leave state untouched
    }
  }

  const handleDownload = () => {
    capturePlatformEvent("qr_code_taped", shareEventProperties())
    downloadDataUrl(result.downloads.png.filename, result.downloads.png.dataUrl)
  }

  return (
    <div
      className="grid gap-5 lg:grid-cols-[320px_minmax(0,1fr)] lg:items-center lg:gap-8"
      data-testid="integration-qr-result"
    >
      {/* Asana 1216779959032975: the generated QR code must stay the main media. */}
      <div
        className="aspect-square w-full max-w-[320px] justify-self-center overflow-hidden lg:justify-self-start"
        data-testid="integration-qr-media"
      >
        <img
          src={result.downloads.png.dataUrl}
          alt={t("integrationInfo.qrCode.previewAlt")}
          className="size-full"
        />
      </div>

      <div className="flex flex-col gap-7">
        <div className="flex items-start gap-4">
          {thumbnailSrc ? (
            <div
              className="shrink-0 overflow-hidden rounded-lg border bg-muted/30"
              data-testid="integration-qr-product-thumb"
            >
              {/* ItemCover handles broken/"<nil>" covers with the standard placeholder.
                  name="" keeps the thumb decorative — the adjacent h3 carries the title. */}
              <ItemCover cover={thumbnailSrc} name="" size={64} />
            </div>
          ) : null}
          <div className="min-w-0">
            <p className="text-sm text-muted-foreground">
              {t("integrationInfo.qrCode.shareSubtitle")}
            </p>
            <h3
              className="mt-2 break-words text-2xl font-bold leading-tight"
              data-testid="integration-qr-title"
            >
              {title}
            </h3>
          </div>
        </div>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold">{t("integrationInfo.qrCode.linkStepTitle")}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("integrationInfo.qrCode.linkStepDescription")}
            </p>
          </div>
          <Button
            onClick={handleCopy}
            size="sm"
            data-testid="integration-qr-copy-link"
            className="w-full shrink-0 bg-vto-accent text-vto-accent-foreground shadow hover:bg-vto-accent/90 focus-visible:ring-vto-accent lg:w-auto"
          >
            {copied ? <Check data-icon="inline-start" /> : <Link2 data-icon="inline-start" />}
            {copied ? t("integrationInfo.qrCode.copied") : t("integrationInfo.qrCode.copyLink")}
          </Button>
        </div>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold">{t("integrationInfo.qrCode.qrStepTitle")}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("integrationInfo.qrCode.qrStepDescription")}
            </p>
          </div>
          <Button
            onClick={handleDownload}
            size="sm"
            data-testid="integration-qr-download"
            className="w-full shrink-0 bg-foreground text-background shadow hover:bg-foreground/90 lg:w-auto"
          >
            <QrCode data-icon="inline-start" />
            {t("integrationInfo.qrCode.downloadQrCode")}
          </Button>
        </div>
      </div>
    </div>
  )
}
